import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import type { IRequestSliceStrategy } from './Images.service';
import type { ImagesInfo } from './ImagesInfo';
import type { ImageStackController, SlicePositionInfo, XRaySlicerParams } from './ImageStackController';
import type * as THREE from 'three';
import type { ViewPortProps } from '@/components/viewPort/ViewPort.tsx';
import type { IClippingManager } from '@/components/clipping/interfaces.ts';

export interface IImagesViewPortController {
  slicesCount: number;
  currentSlice: number;
  contrastWindowLevel: number;
  contrastWindowWidth: number;
  reductionFactorFromOriginal: number;
  slicePosition: number;
  areImagesReady: boolean;
  loadingPercentage: number;
  shouldShowLoadingPercentage: boolean;
  areReducedImagesLoading: boolean;
  imageReductionFactor: number;
  setCurrentSlice: (value: number) => void;
  subscribe: () => void;
  dispose: () => void;
}

export interface IImagesInfo {
  areImagesReady: boolean;
  slicesCount: number;
  currentSlice: number;
  xRaySlicerParams?: XRaySlicerParams;
  sliceDistance: number;
  imageMode: ImagesMode;
  polylineInfo: IImagesPolylineInfo | undefined;
  contrastWindowLevel: number;
  contrastWindowWidth: number;
  reductionFactorFromOriginal: number;
  slicePosition: number;
  loadingPercentage: number;
  shouldShowLoadingPercentage: boolean;
  areReducedImagesLoading: boolean;
  imageReductionFactor: number;
}

export interface IImagesPolylineInfo {
  slicesCount: number;
  currentSlice: number;
  polylinePoints: Array<THREE.Vector3>;
  polylineNormals: Array<THREE.Vector3>;
  setCurrentSlice: (sliceCount: number) => void;
  setInfo: (points: Array<THREE.Vector3>, normals: Array<THREE.Vector3>) => boolean;
}

export enum SliceType {
  AXIAL = 'AXIAL',
  CORONAL = 'CORONAL',
  SAGITTAL = 'SAGITTAL',
  CUSTOM = 'CUSTOM',
  NONE = 'NONE'
}

export enum OrientationType {
  FRONT = 'FRONT'
}

export enum ImagesMode {
  MPR = 'MPR',
  POLYLINE = 'POLYLINE'
}

export enum SlicerMode {
  SINGLE = 'SINGLE',
  AIP = 'AIP',
  FLUORO = 'FLUORO'
}

export interface ImagesViewPortProps extends ViewPortProps {
  sliceType: SliceType;
  isMaximized?: boolean;
  clippingManager?: IClippingManager;
}

export enum ContextEvents {
  NONE = 0,
  REINDICATE = 1,
  REMOVE = 2,
  ADD = 3
}

export interface IImageSliderComponentProps {
  isMaximized: boolean | undefined;
  sliceType: SliceType;
}

export type SliceOrientationsConfig = Map<SliceType, string>;

export type OnSliceCSChangedListener = (sliceCS: THREE.Matrix4, positionInfo: SlicePositionInfo) => void;
export interface IImagesPortContext {
  addOnSliceCSChangedListener: (callback: OnSliceCSChangedListener, immediateNotify?: boolean) => CallbackSubscription;
  readonly sliceDistance: number;
}

export interface IRequestSliceStrategiesProvider {
  getStrategy: (
    imagesMode: ImagesMode,
    imageStackController: ImageStackController,
    imageOrientationInfos: Map<SliceType, ImagesInfo>
  ) => IRequestSliceStrategy;
}

export interface IImageScrollStrategy {
  handleScroll: (delta: number, sliceType: SliceType) => void;
}

export enum ImageScrollMode {
  MPR = 'MPR',
  AXIS = 'AXIS',
  POINTS = 'POINTS'
}
